import { motion } from 'framer-motion'
import {
  ResourceLayout, ResourceHero, ResourceContactCTA, CodeBlock,
  DownloadButton, DownloadRow, StatusBlock, SignatureFooter,
  H2, ACCENTS, MONO, fade,
} from './_shared'

const A = ACCENTS.red

const MU_PLUGIN = `<?php
/**
 * Plugin Name: Hardening Base
 * Description: 6 funcoes essenciais de seguranca WordPress.
 * Path:        wp-content/mu-plugins/hardening.php
 *
 * mu-plugins rodam SEMPRE, antes de qualquer plugin normal.
 * Nao podem ser desativados pela UI. Ideal pra hardening.
 */

if ( ! defined( 'ABSPATH' ) ) exit;

/**
 * 1. Desabilita XML-RPC totalmente.
 * Vetor classico de brute-force e amplificacao DDoS.
 * Se voce nao usa app mobile do WP ou Jetpack legacy, desliga.
 */
add_filter( 'xmlrpc_enabled', '__return_false' );
add_filter( 'wp_headers', function( $headers ) {
    unset( $headers['X-Pingback'] );
    return $headers;
});

/**
 * 2. Bloqueia enumeracao de usuarios via REST API.
 * /wp-json/wp/v2/users vaza usernames pra anonimos.
 * Mantem permissao SO pra usuarios autenticados (Gutenberg/app funcionam).
 */
add_filter( 'rest_endpoints', function( $endpoints ) {
    if ( ! is_user_logged_in() ) {
        if ( isset( $endpoints['/wp/v2/users'] ) ) {
            unset( $endpoints['/wp/v2/users'] );
        }
        if ( isset( $endpoints['/wp/v2/users/(?P<id>[\\\\d]+)'] ) ) {
            unset( $endpoints['/wp/v2/users/(?P<id>[\\\\d]+)'] );
        }
    }
    return $endpoints;
});

/**
 * 3. Esconde erros PHP em producao.
 * Stack traces visiveis sao um mapa pra atacante:
 * mostram caminho de arquivos, versao PHP, plugins em uso.
 */
if ( ! WP_DEBUG ) {
    ini_set( 'display_errors', '0' );
    ini_set( 'display_startup_errors', '0' );
    error_reporting( 0 );
}

/**
 * 4. Bloqueia acesso direto a arquivos sensiveis.
 * wp-config.php, .htaccess, .env, debug.log.
 * Camada extra mesmo que o webserver ja bloqueie.
 */
add_action( 'init', function() {
    $blocked = [ 'wp-config.php', '.htaccess', '.env', 'debug.log' ];
    $request = basename( $_SERVER['REQUEST_URI'] ?? '' );
    if ( in_array( $request, $blocked, true ) ) {
        status_header( 403 );
        die( 'Forbidden' );
    }
});

/**
 * 5. Headers de seguranca em todas as respostas.
 * Defesa em camada contra clickjacking, XSS, MIME sniffing.
 */
add_action( 'send_headers', function() {
    header( 'X-Frame-Options: SAMEORIGIN' );
    header( 'X-Content-Type-Options: nosniff' );
    header( 'Referrer-Policy: strict-origin-when-cross-origin' );
    header( 'Permissions-Policy: camera=(), microphone=(), geolocation=()' );
    if ( is_ssl() ) {
        header( 'Strict-Transport-Security: max-age=31536000; includeSubDomains' );
    }
});

/**
 * 6. Remove versao do WP de todo lugar.
 * generator meta + RSS + scripts/styles enfileirados.
 * Versao conhecida = lista de CVEs do atacante.
 */
remove_action( 'wp_head', 'wp_generator' );
add_filter( 'the_generator', '__return_empty_string' );
add_filter( 'style_loader_src', function( $src ) { return remove_query_arg( 'ver', $src ); }, 9999 );
add_filter( 'script_loader_src', function( $src ) { return remove_query_arg( 'ver', $src ); }, 9999 );
`

const TESTS = [
  {
    title: 'Headers de segurança',
    cmd: 'curl -I https://seusite.com',
    expect: 'Deve mostrar: X-Frame-Options, X-Content-Type-Options: nosniff, Referrer-Policy, e Strict-Transport-Security em HTTPS.',
  },
  {
    title: 'REST API users bloqueada pra anônimos',
    cmd: 'curl https://seusite.com/wp-json/wp/v2/users',
    expect: 'Deve retornar 404 ou rest_no_route. Se voltar lista JSON com usernames, o bloqueio não aplicou.',
  },
  {
    title: 'XML-RPC bloqueado',
    cmd: 'curl https://seusite.com/xmlrpc.php',
    expect: 'Deve retornar erro "XML-RPC services are disabled on this site". Confirma que o filter aplicou.',
  },
  {
    title: 'Versão do WP escondida',
    cmd: 'curl -s https://seusite.com | grep -i "generator"',
    expect: 'Não deve retornar nada. Se aparecer <meta name="generator" content="WordPress 6.x" />, o filter não aplicou.',
  },
]

const INSTALL_CMDS = `# 1. Cria a pasta mu-plugins se nao existir
mkdir -p /var/www/seusite/wp-content/mu-plugins

# 2. Copia o arquivo hardening.php pra la
# (pode usar scp, ftp, ou via wp-cli)

# 3. Permissoes corretas
chown www-data:www-data /var/www/seusite/wp-content/mu-plugins/hardening.php
chmod 644 /var/www/seusite/wp-content/mu-plugins/hardening.php

# 4. Verifica que carregou
wp plugin list --status=must-use
`

export default function MuPluginHardening() {
  return (
    <ResourceLayout>
      <ResourceHero
        tag="recurso · cyber · hard"
        title="WP Hardening: mu-plugin pronto pra produção"
        desc="6 funções PHP que tapam os vetores mais comuns de ataque: XML-RPC, enumeração de users, info disclosure, headers, versão exposta. Roda em mu-plugins, não pode ser desativado pela UI. Versão simplificada do que uso em 9 servidores."
        accent={A}
        source="mu-plugin-hardening"
        word="HARD"
      />

      <StatusBlock accent={A} rows={[
        { k: 'tipo', v: 'cyber · wp hardening' },
        { k: 'formato', v: 'mu-plugin PHP (6 funções) + 4 testes curl' },
        { k: 'esforço', v: '15min upload + 5min testar' },
        { k: 'valor', v: 'tapa XML-RPC, enumeração users, headers, versão WP' },
      ]} />

      <motion.section {...fade(0.05)} style={{ marginBottom: '2rem' }}>
        <div style={{
          padding: '1rem 1.2rem',
          background: A.bg,
          border: `1px solid ${A.border}`,
          borderLeft: `3px solid ${A.solid}`,
          borderRadius: 8,
          display: 'flex', gap: '0.85rem',
        }}>
          <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⚠️</span>
          <div>
            <p style={{ fontFamily: MONO, fontSize: '0.74rem', fontWeight: 600, marginBottom: '0.35rem', color: A.text, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {'// '}atenção · bloqueio rest api /users
            </p>
            <p style={{ fontSize: '0.82rem', color: '#aaa', lineHeight: 1.6 }}>
              O snippet bloqueia <code style={{ fontFamily: MONO, background: '#141414', padding: '0.05rem 0.3rem', borderRadius: 3, color: A.text }}>/wp/v2/users</code> APENAS para anônimos. Usuários logados (Gutenberg, app mobile, REST autenticada) continuam funcionando normalmente. Se você usa app de terceiros que consulta users como anônimo, vai precisar ajustar.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section {...fade(0.08)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>o mu-plugin completo</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          Salva como <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem', color: A.text }}>wp-content/mu-plugins/hardening.php</code>. mu-plugins rodam SEMPRE, antes de qualquer plugin normal, e não podem ser desativados pela UI.
        </p>
        <DownloadRow>
          <DownloadButton content={MU_PLUGIN} filename="hardening.php" accent={A} source="mu-plugin-hardening" mime="application/x-httpd-php" />
        </DownloadRow>
        <CodeBlock lang="php" accent={A} source="mu-plugin-hardening">{MU_PLUGIN}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.11)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>como instalar</H2>
        <CodeBlock lang="bash" accent={A} source="mu-plugin-install">{INSTALL_CMDS}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.14)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>como testar (4 comandos)</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1.25rem' }}>
          Roda cada um substituindo <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem', color: A.text }}>seusite.com</code>. Se algum não passar, o snippet não aplicou ou tem outro plugin sobrescrevendo.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {TESTS.map((t, i) => (
            <div key={i} style={{
              padding: '1rem 1.2rem',
              background: 'linear-gradient(180deg, #111, #0d0d0d)',
              border: '1px solid #1e1e1e',
              borderRadius: 8,
            }}>
              <p style={{ fontFamily: MONO, fontSize: '0.78rem', color: A.text, fontWeight: 600, marginBottom: '0.5rem', letterSpacing: '0.04em' }}>{i + 1}. {t.title}</p>
              <CodeBlock lang="bash" accent={A} source={`mu-plugin-test-${i + 1}`}>{t.cmd}</CodeBlock>
              <p style={{ fontSize: '0.78rem', color: '#aaa', lineHeight: 1.6 }}>
                <b style={{ color: '#fff' }}>Esperado:</b> {t.expect}
              </p>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section {...fade(0.17)} style={{ marginBottom: '2.5rem' }}>
        <div style={{
          padding: '1.2rem 1.4rem',
          background: 'linear-gradient(180deg, #111, #0d0d0d)',
          border: '1px solid #1e1e1e',
          borderLeft: `3px solid ${A.solid}`,
          borderRadius: 10,
        }}>
          <p style={{ fontFamily: MONO, fontSize: '0.72rem', color: A.text, fontWeight: 600, marginBottom: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {'// '}próximo nível
          </p>
          <p style={{ fontSize: '0.82rem', color: '#aaa', lineHeight: 1.65 }}>
            Esse mu-plugin é a versão básica. O stack completo em produção tem: detecção de webshells em uploads, scan diário de checksums vs WP.org, bloqueio de backdoors MySQL, alerta no Slack quando alguém logar como admin de IP novo, e mais 12 verificações automatizadas. Se quiser auditoria do seu WP, me chama.
          </p>
        </div>
      </motion.section>

      <ResourceContactCTA accent={A} source="mu-plugin-hardening" />
      <SignatureFooter accent={A} />
    </ResourceLayout>
  )
}
